import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getVendorToken, setVendorToken, inr, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/ImageUpload";
import { GalleryEditor } from "@/components/GalleryEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Store, LogOut, Plus, Trash2, Pencil, X, Save, CalendarOff, CalendarDays, Wallet,
  Images, User, ClipboardList, Phone, Mail, MapPin, Clock, CheckCircle2, Hourglass, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = ["Stage Decoration", "DJ & Sound", "Shamiyana", "Chairs & Furniture", "Catering", "Photography", "Lighting", "Makeup & Mehandi"];

const TABS = [
  { id: "bookings", label: "Bookings", icon: ClipboardList },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "earnings", label: "Earnings", icon: Wallet },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "profile", label: "Profile", icon: User },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function VendorDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (!getVendorToken()) navigate("/vendor/login");
  }, [navigate]);

  const { data: me, isLoading } = useQuery({
    queryKey: ["vendor-me"],
    queryFn: async () => (await api.get("/vendor/me")).data,
    retry: false,
    onError: () => navigate("/vendor/login"),
  });

  const logout = () => { setVendorToken(null); navigate("/vendor/login"); };

  if (isLoading || !me) return <div className="p-8 text-muted-foreground">Loading portal...</div>;

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <header className="sticky top-0 z-40 glass border-b border-stone-200/60">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-white font-display font-extrabold">M</div>
            <div>
              <p className="font-display font-bold leading-tight">{me.name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Store className="h-3 w-3" /> Vendor portal · {me.city}</p>
            </div>
          </div>
          <button data-testid="vendor-logout" onClick={logout} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-destructive transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
        {/* Desktop tabs */}
        <div className="hidden md:block border-t border-stone-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 flex gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`vtab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 h-12 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        {tab === "bookings" && <BookingsTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "earnings" && <EarningsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "profile" && <ProfileTab me={me} onSaved={() => qc.invalidateQueries({ queryKey: ["vendor-me"] })} />}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-stone-200 grid grid-cols-5" data-testid="vendor-bottom-nav">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`vtab-m-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold ${tab === t.id ? "text-primary" : "text-muted-foreground"}`}>
              <Icon className="h-5 w-5" /> {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
function BookingsTab() {
  const qc = useQueryClient();
  const { data: bookings = [], isLoading } = useQuery({ queryKey: ["vendor-bookings"], queryFn: async () => (await api.get("/vendor/bookings")).data });
  const [active, setActive] = useState(null); // booking for completion dialog
  const [photo, setPhoto] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const openComplete = (b) => { setActive(b); setPhoto(b.completion?.photo || ""); setNote(b.completion?.note || ""); };
  const submit = async () => {
    if (!photo) return toast.error("Please upload a completion photo");
    setSaving(true);
    try {
      await api.post(`/vendor/bookings/${active.id}/complete`, { photo, note });
      toast.success("Sent for admin review");
      setActive(null); setPhoto(""); setNote("");
      qc.invalidateQueries({ queryKey: ["vendor-bookings"] });
    } catch { toast.error("Could not submit"); }
    finally { setSaving(false); }
  };

  const statusBadge = (comp) => {
    const s = comp?.status;
    if (s === "approved") return <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Approved</span>;
    if (s === "pending_review") return <span className="text-xs font-semibold text-amber-600 flex items-center gap-1"><Hourglass className="h-3.5 w-3.5" /> Awaiting review</span>;
    if (s === "rejected") return <span className="text-xs font-semibold text-rose-600">Rejected — re-submit</span>;
    return null;
  };

  if (isLoading) return <p className="text-muted-foreground">Loading bookings…</p>;

  return (
    <section data-testid="vendor-bookings-tab">
      <h2 className="font-display font-bold text-xl mb-1">Your bookings</h2>
      <p className="text-sm text-muted-foreground mb-4">Orders booked for you through Book My Event, with customer contact details.</p>
      {bookings.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="vendor-no-bookings">No bookings yet.</p>
      ) : (
        <div className="grid gap-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`vendor-booking-${b.id}`} className="bg-card border border-stone-200 rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className="text-xs text-primary font-semibold capitalize">{b.event_type || "Event"}</span>
                  <p className="font-display font-bold text-lg leading-tight">{b.customer_name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {fmtDate(b.event_date)}</span>
                    {b.event_time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {b.event_time}</span>}
                    {(b.venue || b.city) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[b.venue, b.city].filter(Boolean).join(", ")}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1">
                    <a href={`tel:${b.customer_phone}`} className="flex items-center gap-1 text-foreground hover:text-primary"><Phone className="h-3.5 w-3.5" /> {b.customer_phone || "—"}</a>
                    <a href={`mailto:${b.customer_email}`} className="flex items-center gap-1 text-foreground hover:text-primary"><Mail className="h-3.5 w-3.5" /> {b.customer_email || "—"}</a>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display font-extrabold text-lg">{inr(b.my_total)}</p>
                  <span className="text-xs text-muted-foreground capitalize">{(b.status || "pending").replace("_", " ")}</span>
                </div>
              </div>
              {b.notes && <p className="text-sm text-muted-foreground mt-2 border-t border-stone-100 pt-2">Note: {b.notes}</p>}
              <div className="mt-3 border-t border-stone-100 pt-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Your items</p>
                <ul className="text-sm space-y-0.5">
                  {b.items.map((it, i) => <li key={i} className="flex justify-between"><span>{it.name} × {it.quantity}</span><span className="text-muted-foreground">{inr((it.price || 0) * (it.quantity || 1))}</span></li>)}
                </ul>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                {statusBadge(b.completion)}
                {b.completion?.status !== "approved" && (
                  <button data-testid={`vendor-complete-${b.id}`} onClick={() => openComplete(b)}
                    className="ml-auto flex items-center gap-1.5 h-9 px-4 bg-primary text-white rounded-full text-sm font-semibold">
                    <CheckCircle2 className="h-4 w-4" /> {b.completion?.status === "pending_review" ? "Update proof" : "Mark complete"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="vendor-complete-dialog">
          <DialogHeader>
            <DialogTitle>Confirm event completion</DialogTitle>
            <DialogDescription>Upload a photo from the finished event. Our team will review and approve it before releasing your settlement.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 mt-1">
            <ImageUpload value={photo} onChange={setPhoto} label="Completion photo" testid="vendor-complete-photo" aspect="aspect-video" />
            <Textarea data-testid="vendor-complete-note" placeholder="Anything to note about the event? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button data-testid="vendor-complete-submit" onClick={submit} disabled={saving || !photo} className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60">
              {saving ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ---------------- Calendar / availability ---------------- */
function CalendarTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["vendor-slots"], queryFn: async () => (await api.get("/vendor/slots")).data });
  const slots = data?.busy_slots || [];
  const allowMultiple = data?.allow_multiple_bookings || false;
  const empty = { date: "", start: "", end: "", reason: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const refresh = () => qc.invalidateQueries({ queryKey: ["vendor-slots"] });

  const add = async () => {
    if (!form.date) return toast.error("Pick a date");
    try {
      await api.post("/vendor/slots", form);
      toast.success("Time blocked");
      setForm(empty); refresh();
    } catch { toast.error("Could not add"); }
  };
  const del = async (id) => { await api.delete(`/vendor/slots/${id}`); toast.success("Removed"); refresh(); };
  const toggleMultiple = async (v) => {
    try { await api.put("/vendor/settings", { allow_multiple_bookings: v }); refresh(); }
    catch { toast.error("Could not update"); }
  };

  const sorted = [...slots].sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`));

  return (
    <section data-testid="vendor-calendar-tab">
      <div className="flex items-center gap-2 mb-1">
        <CalendarOff className="h-5 w-5 text-primary" />
        <h2 className="font-display font-bold text-xl">Availability & busy times</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Block the times you're busy (even part of a day). Bookings made through us are auto-blocked. Customers only see your free times.</p>

      <label className="flex items-center gap-2 text-sm bg-card border border-stone-200 rounded-xl p-3 mb-4" data-testid="vendor-allow-multiple">
        <input type="checkbox" checked={allowMultiple} onChange={(e) => toggleMultiple(e.target.checked)} />
        <span>I can take <b>multiple orders</b> per day — don't hide me from customers when I have a booking.</span>
      </label>

      <div className="bg-card border border-stone-200 rounded-2xl p-4 mb-5 grid sm:grid-cols-5 gap-3 items-end" data-testid="vendor-slot-form">
        <div className="sm:col-span-1">
          <label className="text-xs text-muted-foreground">Date</label>
          <Input data-testid="slot-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">From</label>
          <Input data-testid="slot-start" type="time" value={form.start} onChange={(e) => set("start", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">To</label>
          <Input data-testid="slot-end" type="time" value={form.end} onChange={(e) => set("end", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Reason</label>
          <Input data-testid="slot-reason" placeholder="Optional" value={form.reason} onChange={(e) => set("reason", e.target.value)} />
        </div>
        <button data-testid="slot-add" onClick={add} className="h-10 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-1"><Plus className="h-4 w-4" /> Block</button>
      </div>
      <p className="text-xs text-muted-foreground mb-2">Leave time empty to block the whole day.</p>

      {isLoading ? <p className="text-muted-foreground">Loading…</p> : sorted.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="vendor-no-slots">No busy times — you're open for all bookings.</p>
      ) : (
        <div className="grid gap-2">
          {sorted.map((s) => (
            <div key={s.id} data-testid={`slot-${s.id}`} className="flex items-center gap-3 bg-card border border-stone-200 rounded-xl px-4 py-3">
              <CalendarDays className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{fmtDate(s.date)} {s.start ? `· ${s.start}${s.end ? `–${s.end}` : ""}` : "· All day"}</p>
                <p className="text-xs text-muted-foreground">{s.reason || (s.source === "booking" ? "Booked via Book My Event" : "Blocked")}{s.source === "booking" ? " · Auto" : ""}</p>
              </div>
              <button data-testid={`slot-del-${s.id}`} onClick={() => del(s.id)} className="text-destructive h-8 w-8 grid place-items-center rounded-full hover:bg-muted"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Earnings / settlements ---------------- */
function EarningsTab() {
  const { data, isLoading } = useQuery({ queryKey: ["vendor-settlements"], queryFn: async () => (await api.get("/vendor/settlements")).data });
  if (isLoading || !data) return <p className="text-muted-foreground">Loading…</p>;
  const cards = [
    { l: "Bookings from us", v: data.booking_count, money: false },
    { l: "Total earnings (owed)", v: data.owed_total },
    { l: "Received", v: data.paid_total },
    { l: "Pending settlement", v: data.balance },
  ];
  return (
    <section data-testid="vendor-earnings-tab">
      <h2 className="font-display font-bold text-xl mb-1">Earnings & settlements</h2>
      <p className="text-sm text-muted-foreground mb-4">Your share is calculated after our {data.commission_pct}% commission. Settlements are released after your event completion is approved.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {cards.map((c) => (
          <div key={c.l} className="bg-card border border-stone-200 rounded-2xl p-4">
            <p className="text-xs text-muted-foreground">{c.l}</p>
            <p className="font-display font-extrabold text-xl mt-1">{c.money === false ? c.v : inr(c.v)}</p>
          </div>
        ))}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-5" data-testid="vendor-ready-settlement">
        <p className="text-sm text-emerald-800">Ready for settlement (approved & unpaid)</p>
        <p className="font-display font-extrabold text-2xl text-emerald-700">{inr(data.ready_total)}</p>
      </div>
      <h3 className="font-display font-bold text-lg mb-2">Payments received</h3>
      {(data.payouts || []).length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="vendor-no-payouts">No payments recorded yet.</p>
      ) : (
        <div className="grid gap-2">
          {data.payouts.map((p, i) => (
            <div key={i} className="flex items-center justify-between bg-card border border-stone-200 rounded-xl px-4 py-3">
              <div><p className="font-semibold text-sm">{inr(p.amount)}</p><p className="text-xs text-muted-foreground">{p.note || "Payout"}</p></div>
              <span className="text-xs text-muted-foreground">{fmtDate(p.paid_on)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Gallery / media queue ---------------- */
function GalleryTab() {
  const qc = useQueryClient();
  const { data: media = [], isLoading } = useQuery({ queryKey: ["vendor-media"], queryFn: async () => (await api.get("/vendor/media")).data });
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["vendor-media"] });

  const submit = async (uploadedUrl) => {
    const finalUrl = uploadedUrl || url;
    if (!finalUrl) return;
    try {
      await api.post("/vendor/media", { url: finalUrl, caption });
      toast.success("Photo sent for approval");
      setUrl(""); setCaption(""); refresh();
    } catch { toast.error("Could not upload"); }
  };
  const del = async (id) => { await api.delete(`/vendor/media/${id}`); toast.success("Removed"); refresh(); };

  const badge = (s) => {
    if (s === "approved") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">LIVE ON PROFILE</span>;
    if (s === "rejected") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">REJECTED</span>;
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">AWAITING APPROVAL</span>;
  };

  return (
    <section data-testid="vendor-gallery-tab">
      <h2 className="font-display font-bold text-xl mb-1">Event photos</h2>
      <p className="text-sm text-muted-foreground mb-4">Upload photos from your events. Our team reviews them, and once approved they appear on your public profile automatically.</p>
      <div className="bg-card border border-stone-200 rounded-2xl p-4 mb-5" data-testid="vendor-media-form">
        <ImageUpload value="" onChange={submit} label="Upload an event photo" testid="vendor-media-upload" aspect="aspect-[3/1]" />
        <Input className="mt-3" data-testid="vendor-media-caption" placeholder="Caption (optional) — add before uploading" value={caption} onChange={(e) => setCaption(e.target.value)} />
      </div>
      {isLoading ? <p className="text-muted-foreground">Loading…</p> : media.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="vendor-no-media">No photos uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {media.map((m) => (
            <div key={m.id} data-testid={`vendor-media-${m.id}`} className="relative rounded-2xl overflow-hidden border border-stone-200 bg-card">
              <img src={imgUrl(m.url)} alt={m.caption || "event"} className="w-full aspect-square object-cover" />
              <div className="absolute top-2 left-2">{badge(m.status)}</div>
              <button data-testid={`vendor-media-del-${m.id}`} onClick={() => del(m.id)} className="absolute top-2 right-2 h-7 w-7 grid place-items-center bg-black/60 text-white rounded-full hover:bg-black/80"><X className="h-3.5 w-3.5" /></button>
              {m.caption && <p className="text-xs p-2 truncate">{m.caption}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Profile & services ---------------- */
function ProfileTab({ me, onSaved }) {
  return (
    <div className="space-y-8">
      <ProfileSection me={me} onSaved={onSaved} />
      <ServicesSection services={me.services || []} onChanged={onSaved} />
    </div>
  );
}

function ProfileSection({ me, onSaved }) {
  const [form, setForm] = useState({
    city: me.city || "", cover: me.cover || "", price_range: me.price_range || "₹₹",
    event_types: (me.event_types || []).join(", "), tags: (me.tags || []).join(", "),
    description: me.description || "", gallery: me.gallery || [],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/profile", {
        city: form.city, cover: form.cover, price_range: form.price_range,
        event_types: form.event_types.split(",").map((s) => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        description: form.description, gallery: form.gallery,
      });
      toast.success("Profile updated");
      onSaved();
    } catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  return (
    <section className="bg-card border border-stone-200 rounded-2xl p-5" data-testid="vendor-profile">
      <h2 className="font-display font-bold text-xl mb-4">Business profile</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <ImageUpload value={form.cover} onChange={(url) => setForm({ ...form, cover: url })} label="Cover photo" testid="vendor-cover-upload" />
        </div>
        <Input data-testid="vp-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <Input data-testid="vp-price" placeholder="Price range e.g. ₹₹" value={form.price_range} onChange={(e) => setForm({ ...form, price_range: e.target.value })} />
        <Input data-testid="vp-events" placeholder="Occasions (comma: marriage, reception)" value={form.event_types} onChange={(e) => setForm({ ...form, event_types: e.target.value })} />
        <Input data-testid="vp-tags" placeholder="Tags (comma: Catering, DJ)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
        <Textarea data-testid="vp-desc" className="md:col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="md:col-span-2">
          <p className="text-sm font-semibold mb-1.5">Past events gallery</p>
          <GalleryEditor value={form.gallery} onChange={(g) => setForm({ ...form, gallery: g })} testid="vendor-gallery" />
        </div>
      </div>
      <button data-testid="vp-save" onClick={save} disabled={saving} className="mt-4 flex items-center gap-2 h-10 px-5 bg-primary text-white rounded-full text-sm font-semibold disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save profile"}
      </button>
    </section>
  );
}

const emptyService = { name: "", type: "Stage Decoration", price: "", unit: "per event", image: "", desc: "" };

function ServicesSection({ services, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService);

  const startNew = () => { setForm(emptyService); setEditing("new"); };
  const startEdit = (s) => { setForm({ ...s, price: String(s.price) }); setEditing(s.id); };
  const cancel = () => { setEditing(null); setForm(emptyService); };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    const payload = { name: form.name, type: form.type, price: Number(form.price), unit: form.unit, image: form.image, desc: form.desc };
    try {
      if (editing === "new") await api.post("/vendor/services", payload);
      else await api.put(`/vendor/services/${editing}`, payload);
      toast.success("Service saved");
      cancel(); onChanged();
    } catch { toast.error("Could not save service"); }
  };
  const del = async (id) => {
    try { await api.delete(`/vendor/services/${id}`); toast.success("Service removed"); onChanged(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <section data-testid="vendor-services">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Your services</h2>
        <button data-testid="vendor-add-service" onClick={startNew} className="flex items-center gap-1.5 h-10 px-4 bg-primary text-white rounded-full text-sm font-semibold">
          <Plus className="h-4 w-4" /> Add service
        </button>
      </div>

      {editing && (
        <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-5 grid md:grid-cols-2 gap-4" data-testid="vendor-service-form">
          <div className="md:col-span-2">
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Service photo" testid="vendor-service-image-upload" aspect="aspect-[3/1]" />
          </div>
          <Input data-testid="vs-name" placeholder="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="vs-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
            {SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <Input data-testid="vs-price" type="number" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <Input data-testid="vs-unit" placeholder="Unit e.g. per event / per plate" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          <Textarea data-testid="vs-desc" className="md:col-span-2" placeholder="Description" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} />
          <div className="md:col-span-2 flex gap-2">
            <button data-testid="vs-save" onClick={save} className="flex items-center gap-2 h-10 px-5 bg-primary text-white rounded-full text-sm font-semibold"><Save className="h-4 w-4" /> Save</button>
            <button data-testid="vs-cancel" onClick={cancel} className="flex items-center gap-2 h-10 px-5 border border-stone-300 rounded-full text-sm font-semibold"><X className="h-4 w-4" /> Cancel</button>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="vendor-no-services">No services yet. Add your first one!</p>
      ) : (
        <div className="grid gap-3">
          {services.map((s) => (
            <div key={s.id} data-testid={`vendor-service-${s.id}`} className="flex items-center gap-3 bg-card border border-stone-200 rounded-2xl p-3">
              {s.image ? <img src={imgUrl(s.image)} alt={s.name} className="h-16 w-16 rounded-xl object-cover shrink-0" /> : <div className="h-16 w-16 rounded-xl bg-muted shrink-0" />}
              <div className="flex-1 min-w-0">
                <span className="overline text-primary">{s.type}</span>
                <p className="font-semibold leading-tight">{s.name}</p>
                <p className="text-sm font-bold">{inr(s.price)} <span className="text-xs font-normal text-muted-foreground">{s.unit}</span></p>
              </div>
              <button data-testid={`vendor-edit-${s.id}`} onClick={() => startEdit(s)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`vendor-del-${s.id}`} onClick={() => del(s.id)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
